import axios, { AxiosResponse } from "axios";
import { AUDIT_EVENT_API } from "../config";

export interface AuditEvent {
  id: number;
  createdAt: string;
  eventType: string;
  sourceId?: string;
  targetId?: string;
  data?: string;
}

export interface AuditLogResponse {
  data: AuditEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AuditLogFilters {
  startDate?: Date;
  endDate?: Date;
  eventTypes?: string[];
  sourceId?: string;
  targetId?: string;
}

export const getAuditLogs = async (
  page: number,
  pageSize: number,
  sortField: string = "createdAt",
  sortDirection: string = "desc",
  filters: AuditLogFilters = {}
): Promise<AxiosResponse<AuditLogResponse>> => {
  const params: any = {
    page,
    pageSize,
    sortField,
    sortDirection,
  };

  if (filters.startDate) {
    params.startDate = filters.startDate.toISOString();
  }

  if (filters.endDate) {
    params.endDate = filters.endDate.toISOString();
  }

  if (filters.eventTypes && filters.eventTypes.length > 0) {
    params.eventTypes = filters.eventTypes.join(',');
  }

  if (filters.sourceId) {
    params.sourceId = filters.sourceId;
  }

  if (filters.targetId) {
    params.targetId = filters.targetId;
  }

  return axios.get(AUDIT_EVENT_API, { params });
};

export interface AuditEventTypeInfo {
  value: string;
  label: string;
  group: "Sales" | "Stock" | "Products" | "Xero" | "Bills";
  color: string;
}

/**
 * Every event type the server writes, with the label, group and colour the
 * audit page uses. Order here is the order in the filter picker.
 */
export const AUDIT_EVENT_TYPES: AuditEventTypeInfo[] = [
  { value: "SALE", label: "Sale", group: "Sales", color: "#ff4444" },
  { value: "REFUND", label: "Refund", group: "Sales", color: "#ff8800" },
  { value: "CANCELLATION", label: "Cancellation", group: "Sales", color: "#cc6600" },
  { value: "DELETION", label: "Order deletion", group: "Sales", color: "#996633" },

  { value: "ADJUST", label: "Stock adjustment", group: "Stock", color: "#d4b800" },
  { value: "STOCK_RECEIVE", label: "Stock received", group: "Stock", color: "#0cb10c" },
  { value: "RECEIPT_REVERSAL", label: "Receipt reversal", group: "Stock", color: "#10b5b5" },
  { value: "STOCKTAKE_APPLIED", label: "Stocktake applied", group: "Stock", color: "#00aa44" },
  { value: "STOCK_DISCREPANCY_CHECK_SUCCESS", label: "Stock discrepancy check OK", group: "Stock", color: "#4400ff" },
  { value: "STOCK_DISCREPANCY_CHECK_ERROR", label: "Stock discrepancy check error", group: "Stock", color: "#a143f3" },

  { value: "PRODUCT_CREATED", label: "Product created", group: "Products", color: "#2e8b57" },
  { value: "PRODUCT_DELETED", label: "Product deleted", group: "Products", color: "#8b0000" },
  { value: "VARIANT_ADDED", label: "Variant added", group: "Products", color: "#3cb371" },
  { value: "VARIANT_REMOVED", label: "Variant removed", group: "Products", color: "#b22222" },
  { value: "PRODUCT_LOCATION_MOVE", label: "Location move", group: "Products", color: "#b8860b" },
  { value: "PRODUCT_SUPPLIER_UPDATE", label: "Supplier update", group: "Products", color: "#8b4513" },
  { value: "PRODUCT_TAGS_BULK_UPDATE", label: "Tags bulk update", group: "Products", color: "#6a5acd" },
  { value: "PRODUCT_SEARCH", label: "Product search", group: "Products", color: "#0088ff" },

  { value: "XERO_BILL_CREATED", label: "Bill created", group: "Xero", color: "#ff0088" },
  { value: "XERO_BILL_CREATION_FAILED", label: "Bill creation failed", group: "Xero", color: "#cc0066" },
  { value: "XERO_BILL_RETRY_SUCCESS", label: "Bill retry succeeded", group: "Xero", color: "#e0409a" },
  { value: "XERO_BILL_RETRY_FAILED", label: "Bill retry failed", group: "Xero", color: "#99004d" },
  { value: "XERO_PO_STATUS_UPDATED", label: "PO status updated", group: "Xero", color: "#ff6600" },

  { value: "BILL_AUTO_CLEANED", label: "Auto-cleaned", group: "Bills", color: "#888888" },
  { value: "BILL_CLEANUP_404", label: "Cleanup 404", group: "Bills", color: "#ff6666" },
  { value: "BILL_ROLLBACK_FAILED", label: "Rollback failed", group: "Bills", color: "#cc0000" },
  { value: "BILL_BULK_RETRY", label: "Bulk retry", group: "Bills", color: "#9966ff" },
  { value: "BILL_SYNC_STATUS", label: "Sync status", group: "Bills", color: "#0099cc" },
  { value: "BILL_MERGED", label: "Merged", group: "Bills", color: "#ff66cc" },
];

export const getEventTypes = (): string[] => AUDIT_EVENT_TYPES.map((t) => t.value);

export const getEventTypeColor = (eventType: string): string =>
  AUDIT_EVENT_TYPES.find((t) => t.value === eventType)?.color ?? "#000000";
